import React, { useEffect, useState, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { FileTextIcon, CheckCircleIcon, XCircleIcon, ClockIcon, PercentIcon, MapPinIcon, BuildingIcon, CalendarIcon, ChevronDownIcon, PencilIcon, SearchIcon } from 'lucide-react';
import { BIKPI } from './BIKPI';
import { BICard } from './BICard';
import { BIInsights } from './BIInsights';
import { trendData, quotesListData, userInsights } from '../../data/biData';
const COLORS = ['#10b981', '#3b82f6', '#ef4444', '#64748b'];
type DateFilterMode = 'year' | 'quarter' | 'month';
const currentYear = new Date().getFullYear();
const years = Array.from({
  length: 6
}, (_, i) => currentYear - i);
const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const salesManagersByRegion: Record<string, {
  name: string;
  email: string;
  initials: string;
}[]> = {
  'Aisco MENA': [{
    name: 'John Quirk',
    email: 'john.quirk@aisco.com',
    initials: 'JQ'
  }, {
    name: 'Mohamed S. Aloraini',
    email: 'mohamed.aloraini@aisco.com',
    initials: 'MA'
  }, {
    name: 'Abdullah ALkaff',
    email: 'abdullah.alkaff@aisco.com',
    initials: 'AA'
  }],
  'Aisco AFRICA': [{
    name: 'Khaled Shalabi',
    email: 'khaled.shalabi@aisco.com',
    initials: 'KS'
  }, {
    name: 'Baher Fahem Alani',
    email: 'baher.alani@aisco.com',
    initials: 'BA'
  }, {
    name: 'Essam Fakhry Mohamed',
    email: 'essam.mohamed@aisco.com',
    initials: 'EM'
  }],
  'Aisco EUROPE': [{
    name: 'Régis SIMIER',
    email: 'regis.simier@aisco.com',
    initials: 'RS'
  }, {
    name: 'Javier Adrados',
    email: 'javier.adrados@aisco.com',
    initials: 'JA'
  }, {
    name: 'Daniel Fernández',
    email: 'daniel.fernandez@aisco.com',
    initials: 'DF'
  }, {
    name: 'Olexandr Morozov',
    email: 'olexandr.morozov@aisco.com',
    initials: 'OM'
  }],
  'Aisco AMERICA': [{
    name: 'Diego Solis',
    email: 'diego.solis@aisco.com',
    initials: 'DS'
  }, {
    name: 'Chris Spelic',
    email: 'chris.spelic@aisco.com',
    initials: 'CS'
  }, {
    name: 'Mark Ellis',
    email: 'mark.ellis@aisco.com',
    initials: 'ME'
  }],
  'Aisco ANZ': [{
    name: 'John Quirk',
    email: 'john.quirk@aisco.com',
    initials: 'JQ'
  }]
};
const allRegions = Object.keys(salesManagersByRegion);
export function UserPerformanceView() {
  const [selectedRegion, setSelectedRegion] = useState('Aisco AMERICA');
  const [selectedManagerName, setSelectedManagerName] = useState('Diego Solis');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dateMode, setDateMode] = useState<DateFilterMode>('year');
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedQuarter, setSelectedQuarter] = useState('Q1');
  const [selectedMonth, setSelectedMonth] = useState('January');
  const [isEditingDate, setIsEditingDate] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const dateLabel = dateMode === 'year' ? `${selectedYear}` : dateMode === 'quarter' ? `${selectedQuarter} ${selectedYear}` : `${selectedMonth} ${selectedYear}`;
  const currentManager = Object.values(salesManagersByRegion).flat().find(m => m.name === selectedManagerName) || salesManagersByRegion['Aisco AMERICA'][0];
  const managersForRegion = selectedRegion === 'All' ? Object.values(salesManagersByRegion).flat() : salesManagersByRegion[selectedRegion] || [];
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setIsEditingDate(false);
      }
    }
    if (isDropdownOpen || isEditingDate) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen, isEditingDate]);
  const handleSelectManager = (manager: {
    name: string;
    email: string;
    initials: string;
  }, region: string) => {
    setSelectedManagerName(manager.name);
    if (region !== selectedRegion && selectedRegion !== 'All') {
      setSelectedRegion(region);
    }
    setIsDropdownOpen(false);
  };
  const handleRegionChange = (region: string) => {
    setSelectedRegion(region);
    const managers = region === 'All' ? Object.values(salesManagersByRegion).flat() : salesManagersByRegion[region] || [];
    if (managers.length > 0 && !managers.find(m => m.name === selectedManagerName)) {
      setSelectedManagerName(managers[0].name);
    }
  };
  const statusData = [{
    name: 'Won',
    value: 85
  }, {
    name: 'Active',
    value: 15
  }, {
    name: 'Lost',
    value: 15
  }, {
    name: 'Draft',
    value: 5
  }];
  const userTrendData = trendData.map(d => ({
    ...d,
    total: Math.floor(d.total * 0.15),
    won: Math.floor(d.won * 0.15),
    lost: Math.floor(d.lost * 0.15)
  }));
  return <div className="space-y-6" data-id="element-966">
      {/* Access/Coverage Block */}
      <div className="flex flex-wrap items-center gap-4" data-id="element-967">
        {/* User Identity — now a selector */}
        <div className="flex items-center gap-4 mr-auto relative" ref={dropdownRef} data-id="element-968">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center ring-2 ring-blue-400/30 shadow-lg flex-shrink-0" data-id="element-969">
            <span className="text-lg font-bold text-white" data-id="element-970">
              {currentManager.initials}
            </span>
          </div>
          <div data-id="element-971">
            {/* Clickable name selector */}
            <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="flex items-center gap-2 group text-left" data-id="element-972">
              <h3 className="text-white font-bold text-xl leading-tight group-hover:text-blue-200 transition-colors" data-id="element-973">
                {currentManager.name}
              </h3>
              <ChevronDownIcon size={18} className={`text-blue-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} data-id="element-974" />
            </button>
            <p className="text-blue-300/70 text-sm leading-tight mt-0.5" data-id="element-975">
              Territory Manager
            </p>
            <p className="text-blue-300/50 text-xs leading-tight mt-0.5" data-id="element-976">
              {currentManager.email}
            </p>

            {/* Inline date filter */}
            <div className="relative mt-1" ref={datePickerRef} data-id="element-977">
              <button onClick={() => setIsEditingDate(!isEditingDate)} className="flex items-center gap-2 text-sm text-blue-300 hover:text-white transition-colors group" data-id="element-978">
                <CalendarIcon size={15} className="text-blue-400/70 group-hover:text-blue-300" data-id="element-979" />
                <span className="font-medium" data-id="element-980">{dateLabel}</span>
                <PencilIcon size={12} className="opacity-0 group-hover:opacity-100 transition-opacity text-blue-400" data-id="element-981" />
              </button>

              {/* Date picker popover */}
              {isEditingDate && <div className="absolute top-full left-0 mt-2 bg-[#0f1d3a] rounded-xl border border-white/10 shadow-2xl p-3 z-50 min-w-[240px]" data-id="element-982">
                  <div className="space-y-2.5" data-id="element-983">
                    {/* Period type */}
                    <div data-id="element-984">
                      <p className="text-[10px] uppercase tracking-wider text-blue-400/60 mb-1" data-id="element-985">
                        Period
                      </p>
                      <div className="flex gap-1" data-id="element-986">
                        {(['year', 'quarter', 'month'] as DateFilterMode[]).map(mode => <button key={mode} onClick={() => setDateMode(mode)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${dateMode === mode ? 'bg-blue-600 text-white' : 'bg-white/5 text-blue-300 hover:bg-white/10 hover:text-white'}`} data-id="element-987">
                              {mode.charAt(0).toUpperCase() + mode.slice(1)}
                            </button>)}
                      </div>
                    </div>

                    {/* Month selector */}
                    {dateMode === 'month' && <div data-id="element-988">
                        <p className="text-[10px] uppercase tracking-wider text-blue-400/60 mb-1" data-id="element-989">
                          Month
                        </p>
                        <div className="relative" data-id="element-990">
                          <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/10 rounded-lg px-3 py-1.5 pr-7 text-xs font-medium text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all cursor-pointer" data-id="element-991">
                            {months.map(m => <option key={m} value={m} className="bg-slate-800 text-white" data-id="element-992">
                                {m}
                              </option>)}
                          </select>
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5 text-blue-300" data-id="element-993">
                            <ChevronDownIcon size={12} data-id="element-994" />
                          </div>
                        </div>
                      </div>}

                    {/* Quarter selector */}
                    {dateMode === 'quarter' && <div data-id="element-995">
                        <p className="text-[10px] uppercase tracking-wider text-blue-400/60 mb-1" data-id="element-996">
                          Quarter
                        </p>
                        <div className="flex gap-1" data-id="element-997">
                          {quarters.map(q => <button key={q} onClick={() => setSelectedQuarter(q)} className={`flex-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${selectedQuarter === q ? 'bg-blue-600 text-white' : 'bg-white/5 text-blue-300 hover:bg-white/10 hover:text-white'}`} data-id="element-998">
                              {q}
                            </button>)}
                        </div>
                      </div>}

                    {/* Year selector */}
                    <div data-id="element-999">
                      <p className="text-[10px] uppercase tracking-wider text-blue-400/60 mb-1" data-id="element-1000">
                        Year
                      </p>
                      <div className="relative" data-id="element-1001">
                        <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))} className="w-full appearance-none bg-white/10 border border-white/10 rounded-lg px-3 py-1.5 pr-7 text-xs font-medium text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all cursor-pointer" data-id="element-1002">
                          {years.map(y => <option key={y} value={y} className="bg-slate-800 text-white" data-id="element-1003">
                              {y}
                            </option>)}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5 text-blue-300" data-id="element-1004">
                          <ChevronDownIcon size={12} data-id="element-1005" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>}
            </div>

            {/* Dropdown */}
            {isDropdownOpen && <div className="absolute top-full left-0 mt-2 bg-[#0f1d3a] rounded-xl border border-white/10 shadow-2xl z-50 min-w-[340px] max-h-[420px] overflow-hidden flex flex-col" data-id="element-1006">
                {/* Region filter tabs */}
                <div className="px-3 pt-3 pb-2 border-b border-white/10" data-id="element-1007">
                  <p className="text-[10px] uppercase tracking-wider text-blue-400/60 mb-2" data-id="element-1008">
                    Region
                  </p>
                  <div className="flex flex-wrap gap-1" data-id="element-1009">
                    <button onClick={() => handleRegionChange('All')} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${selectedRegion === 'All' ? 'bg-blue-600 text-white' : 'bg-white/5 text-blue-300 hover:bg-white/10 hover:text-white'}`} data-id="element-1010">
                      All
                    </button>
                    {allRegions.map(region => <button key={region} onClick={() => handleRegionChange(region)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${selectedRegion === region ? 'bg-blue-600 text-white' : 'bg-white/5 text-blue-300 hover:bg-white/10 hover:text-white'}`} data-id="element-1011">
                        {region.replace('Aisco ', '')}
                      </button>)}
                  </div>
                </div>

                {/* Search bar */}
                <div className="px-3 pt-2 pb-1 border-b border-white/10" data-id="element-1012">
                  <div className="relative" data-id="element-1013">
                    <SearchIcon size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-blue-400/50" data-id="element-1014" />
                    <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search by name or email..." className="w-full bg-white/5 border border-white/10 rounded-lg pl-8 pr-3 py-1.5 text-xs text-white placeholder-blue-300/40 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" autoFocus data-id="element-1015" />
                  </div>
                </div>

                {/* Sales managers list */}
                <div className="overflow-y-auto flex-1 py-1 no-scrollbar" data-id="element-1016">
                  {selectedRegion === 'All' ? Object.entries(salesManagersByRegion).map(([region, managers]) => {
                const filtered = managers.filter(m => m.name.toLowerCase().includes(searchQuery.toLowerCase()) || m.email.toLowerCase().includes(searchQuery.toLowerCase()));
                if (filtered.length === 0) return null;
                return <div key={region} data-id="element-1017">
                              <p className="px-4 pt-2.5 pb-1 text-[10px] uppercase tracking-wider text-blue-400/50 font-medium" data-id="element-1018">
                                {region}
                              </p>
                              {filtered.map(manager => <button key={`${region}-${manager.name}`} onClick={() => handleSelectManager(manager, region)} className={`w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-white/5 transition-colors ${selectedManagerName === manager.name ? 'bg-white/5' : ''}`} data-id="element-1019">
                                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0" data-id="element-1020">
                                    <span className="text-[10px] font-bold text-white" data-id="element-1021">
                                      {manager.initials}
                                    </span>
                                  </div>
                                  <div className="flex-1 min-w-0" data-id="element-1022">
                                    <p className={`text-sm font-medium truncate ${selectedManagerName === manager.name ? 'text-white' : 'text-blue-200'}`} data-id="element-1023">
                                      {manager.name}
                                    </p>
                                    <p className="text-xs text-blue-300/50 truncate" data-id="element-1024">
                                      {manager.email}
                                    </p>
                                  </div>
                                  {selectedManagerName === manager.name && <div className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" data-id="element-1025" />}
                                </button>)}
                            </div>;
              }) : managersForRegion.filter(m => m.name.toLowerCase().includes(searchQuery.toLowerCase()) || m.email.toLowerCase().includes(searchQuery.toLowerCase())).map(manager => <button key={manager.name} onClick={() => handleSelectManager(manager, selectedRegion)} className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-white/5 transition-colors ${selectedManagerName === manager.name ? 'bg-white/5' : ''}`} data-id="element-1026">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0" data-id="element-1027">
                              <span className="text-[10px] font-bold text-white" data-id="element-1028">
                                {manager.initials}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-1029">
                              <p className={`text-sm font-medium truncate ${selectedManagerName === manager.name ? 'text-white' : 'text-blue-200'}`} data-id="element-1030">
                                {manager.name}
                              </p>
                              <p className="text-xs text-blue-300/50 truncate" data-id="element-1031">
                                {manager.email}
                              </p>
                            </div>
                            {selectedManagerName === manager.name && <div className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" data-id="element-1032" />}
                          </button>)}
                </div>
              </div>}
          </div>
        </div>

        {/* Assigned Business Unit Panel */}
        <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl border border-white/10 px-5 py-3" data-id="element-1033">
          <p className="text-blue-300/70 text-xs mb-2 font-medium" data-id="element-1034">
            Assigned Business Units
          </p>
          <div className="flex gap-2" data-id="element-1035">
            <span className="px-2.5 py-1 bg-white/5 border border-white/10 rounded-lg text-xs text-blue-200 font-medium" data-id="element-1036">
              Aisco AMERICA
            </span>
            <span className="px-2.5 py-1 bg-white/5 border border-white/10 rounded-lg text-xs text-blue-200 font-medium" data-id="element-1037">
              Aisco LATAM
            </span>
          </div>
        </div>

        {/* Coverage Panel */}
        <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl border border-white/10 px-5 py-3" data-id="element-1038">
          <p className="text-blue-300/70 text-xs mb-2 font-medium" data-id="element-1039">Coverage</p>
          <div className="flex gap-4" data-id="element-1040">
            <div className="flex items-center text-sm text-blue-200" data-id="element-1041">
              <MapPinIcon size={14} className="mr-1.5 text-blue-400" data-id="element-1042" />
              <span className="font-semibold text-white mr-1" data-id="element-1043">4</span> Countries
            </div>
            <div className="flex items-center text-sm text-blue-200" data-id="element-1044">
              <BuildingIcon size={14} className="mr-1.5 text-blue-400" data-id="element-1045" />
              <span className="font-semibold text-white mr-1" data-id="element-1046">12</span> Dealers
            </div>
          </div>
        </div>
      </div>

      {/* KPIs Section */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-1047">
        <BIKPI title="Total" value={120} trend={12} color="blue" data-id="element-1048" />
        <BIKPI title="Won" value={85} trend={8} color="emerald" data-id="element-1049" />
        <BIKPI title="Win Rate" value="85.0%" trend={2.5} color="teal" data-id="element-1050" />
        <BIKPI title="Active" value={15} color="amber" data-id="element-1051" />
        <BIKPI title="Lost" value={15} color="red" data-id="element-1052" />
        <BIKPI title="Draft" value={5} color="purple" data-id="element-1053" />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1054">
        <BICard title="Quotes by Status" className="lg:col-span-1" data-id="element-1055">
          <div className="h-64 flex flex-col items-center justify-center" data-id="element-1056">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1057">
              <PieChart data-id="element-1058">
                <Pie data={statusData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value" data-id="element-1059">
                  {statusData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-1060" />)}
                </Pie>
                <Tooltip contentStyle={{
                backgroundColor: '#0f1d3a',
                borderColor: '#ffffff20',
                borderRadius: '8px',
                color: '#fff'
              }} itemStyle={{
                color: '#fff'
              }} data-id="element-1061" />
                <Legend wrapperStyle={{
                fontSize: '12px',
                color: '#93c5fd'
              }} data-id="element-1062" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </BICard>

        <BICard title="Personal Quotes Trend" className="lg:col-span-2" data-id="element-1063">
          <div className="h-64" data-id="element-1064">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1065">
              <LineChart data={userTrendData} margin={{
              top: 20,
              right: 30,
              left: 0,
              bottom: 5
            }} data-id="element-1066">
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} data-id="element-1067" />
                <XAxis dataKey="month" stroke="#93c5fd" fontSize={12} tickLine={false} axisLine={false} data-id="element-1068" />
                <YAxis stroke="#93c5fd" fontSize={12} tickLine={false} axisLine={false} data-id="element-1069" />
                <Tooltip contentStyle={{
                backgroundColor: '#0f1d3a',
                borderColor: '#ffffff20',
                borderRadius: '8px',
                color: '#fff'
              }} data-id="element-1070" />
                <Legend wrapperStyle={{
                fontSize: '12px',
                color: '#93c5fd'
              }} data-id="element-1071" />
                <Line type="monotone" dataKey="total" name="Total" stroke="#3b82f6" strokeWidth={2} dot={{
                r: 3
              }} data-id="element-1072" />
                <Line type="monotone" dataKey="won" name="Won" stroke="#10b981" strokeWidth={2} dot={{
                r: 3
              }} data-id="element-1073" />
                <Line type="monotone" dataKey="lost" name="Lost" stroke="#ef4444" strokeWidth={2} dot={{
                r: 3
              }} data-id="element-1074" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </BICard>
      </div>

      {/* Tables & Insights Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1075">
        <div className="lg:col-span-2 space-y-6" data-id="element-1076">
          <BICard title="Recent Quotes" data-id="element-1077">
            <div className="overflow-x-auto" data-id="element-1078">
              <table className="w-full text-sm text-left" data-id="element-1079">
                <thead className="text-xs text-blue-300 uppercase bg-white/5 border-b border-white/10" data-id="element-1080">
                  <tr data-id="element-1081">
                    <th className="px-4 py-3 font-medium" data-id="element-1082">Quote #</th>
                    <th className="px-4 py-3 font-medium" data-id="element-1083">Date</th>
                    <th className="px-4 py-3 font-medium" data-id="element-1084">Dealer</th>
                    <th className="px-4 py-3 font-medium" data-id="element-1085">Country</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-1086">Amount</th>
                    <th className="px-4 py-3 font-medium text-center" data-id="element-1087">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-1088">
                  {quotesListData.filter(q => q.user === 'Alice Smith').map((quote, idx) => <tr key={idx} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-id="element-1089">
                        <td className="px-4 py-3 font-medium text-blue-400" data-id="element-1090">
                          {quote.id}
                        </td>
                        <td className="px-4 py-3 text-blue-200" data-id="element-1091">
                          {quote.date}
                        </td>
                        <td className="px-4 py-3 text-white" data-id="element-1092">{quote.dealer}</td>
                        <td className="px-4 py-3 text-blue-300" data-id="element-1093">
                          {quote.country}
                        </td>
                        <td className="px-4 py-3 text-right text-white" data-id="element-1094">
                          ${quote.amount.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-center" data-id="element-1095">
                          <span className={`px-2 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider ${quote.status === 'Won' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : quote.status === 'Active' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : quote.status === 'Lost' ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-slate-500/20 text-slate-400 border border-slate-500/30'}`} data-id="element-1096">
                            {quote.status}
                          </span>
                        </td>
                      </tr>)}
                </tbody>
              </table>
            </div>
          </BICard>
        </div>

        <div className="lg:col-span-1" data-id="element-1097">
          <BIInsights insights={userInsights} data-id="element-1098" />
        </div>
      </div>
    </div>;
}